import { useEffect, useState } from "react";
import { LogIn, LogOut, Coffee, Undo2, MapPin, Loader2, Clock, ChevronDown, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const LABELS = { arrivee: "Arrivée", pause: "Pause", retour: "Retour de pause", depart: "Départ" };

const ACTIONS = [
  { type: "arrivee", label: "Je suis arrivé", icon: LogIn, color: "bg-green-600 hover:bg-green-700" },
  { type: "pause", label: "Pause", icon: Coffee, color: "bg-amber-500 hover:bg-amber-600" },
  { type: "retour", label: "Retour de pause", icon: Undo2, color: "bg-blue-600 hover:bg-blue-700" },
  { type: "depart", label: "Je pars", icon: LogOut, color: "bg-red-600 hover:bg-red-700" },
];

function hhmm(iso) {
  try { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error("La géolocalisation n'est pas disponible sur cet appareil."));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(new Error(err.code === 1 ? "Autorisez la localisation pour pouvoir pointer." : "Impossible d'obtenir votre position. Réessayez.")),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
    );
  });
}

export function PointageCard() {
  const [today, setToday] = useState(null);
  const [loadingType, setLoadingType] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState(null);

  const load = () => api.get("/attendance/today").then(({ data }) => setToday(data)).catch(() => setToday({ punches: [], state: { allowed: ["arrivee"] } }));
  useEffect(() => { load(); }, []);

  const punch = async (type) => {
    setLoadingType(type);
    try {
      const { lat, lng } = await getPosition();
      const { data } = await api.post("/attendance/punch", { type, lat, lng });
      setToday((t) => ({ ...t, punches: [...(t?.punches || []), data.punch], state: data.state }));
      toast.success(`${LABELS[type]} enregistrée à ${hhmm(data.punch.time)}`);
    } catch (err) {
      toast.error(err.response ? formatApiError(err.response?.data?.detail) : err.message);
    } finally { setLoadingType(null); }
  };

  const toggleHistory = () => {
    setShowHistory((v) => !v);
    if (history === null) api.get("/attendance/history").then(({ data }) => setHistory(data)).catch(() => setHistory([]));
  };

  const state = today?.state || { allowed: [] };
  const punches = today?.punches || [];

  return (
    <div data-testid="pointage-card" className="bg-white border border-brand-line rounded-2xl p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-brand/15 text-brand flex items-center justify-center"><Clock className="w-5 h-5" /></span>
          <div>
            <h2 className="font-heading font-bold text-brand-ink">Pointage du jour</h2>
            <p className="text-xs text-brand-muted">Pointez sur place — la localisation est vérifiée.</p>
          </div>
        </div>
        {state.departed && <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 rounded-full px-3 py-1"><CheckCircle2 className="w-3.5 h-3.5" /> Journée terminée</span>}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
        {ACTIONS.map((a) => {
          const enabled = state.allowed?.includes(a.type);
          const done = punches.some((p) => p.type === a.type);
          const busy = loadingType === a.type;
          return (
            <button
              key={a.type}
              data-testid={`punch-${a.type}`}
              disabled={!enabled || busy || !!loadingType}
              onClick={() => punch(a.type)}
              className={`relative rounded-xl px-3 py-4 font-semibold text-white text-sm flex flex-col items-center gap-2 transition-all ${enabled ? a.color : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}
            >
              {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <a.icon className="w-6 h-6" />}
              <span className="leading-tight text-center">{a.label}</span>
              {done && <span className="absolute top-1.5 right-1.5"><CheckCircle2 className="w-4 h-4 text-white/90" /></span>}
            </button>
          );
        })}
      </div>

      {punches.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2" data-testid="pointage-timeline">
          {punches.map((p) => (
            <span key={p.id} className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-ink bg-brand-cream rounded-full px-3 py-1.5">
              <MapPin className="w-3.5 h-3.5 text-brand" /> {LABELS[p.type]} · {hhmm(p.time)}
            </span>
          ))}
        </div>
      )}

      <button onClick={toggleHistory} data-testid="pointage-history-toggle" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:gap-2 transition-all">
        Mon historique <ChevronDown className={`w-4 h-4 transition-transform ${showHistory ? "rotate-180" : ""}`} />
      </button>

      {showHistory && (
        <div className="mt-3 space-y-2" data-testid="pointage-history">
          {history === null ? <Loader2 className="w-5 h-5 animate-spin text-brand" />
            : history.length === 0 ? <p className="text-sm text-brand-muted">Aucun pointage enregistré.</p>
            : history.map((d) => (
              <div key={d.date} className="border border-brand-line rounded-xl px-4 py-3">
                <p className="text-sm font-semibold text-brand-ink">{new Date(d.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {d.punches.map((p) => (
                    <span key={p.id} className="text-xs text-brand-sub bg-brand-cream rounded-full px-2.5 py-1">{LABELS[p.type]} {hhmm(p.time)}</span>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
